import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch, Link } from 'react-router-dom'
import {
  Button,
  Table,
  Tag,
  Space,
  Empty,
  Select,
  Input,
  DatePicker,
  Modal,
  Checkbox
} from 'antd'
import {
  ReloadOutlined,
  HistoryOutlined,
  ExportOutlined
} from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import { getRequest, formatUrl, dutyConversationsUrl } from '../../../config'
import {
  CONV_STATE_COLORS as STATE_COLORS,
  CHANNEL_OPTIONS,
  carStatusTag,
  channelTag,
  serviceLabel
} from '../statusColors'
import IdentityLabel from '../../../components/IdentityLabel'
import CustomerDetailBody from '../Customers/CustomerDetailBody'
import ConversationDetail from './Detail'

const { RangePicker } = DatePicker

const STATE_OPTIONS = Object.keys(STATE_COLORS).map(s => ({
  value: s,
  label: s
}))

// Build a querystring from the active filters, omitting blanks. Dates are sent as ISO so the
// backend DateTimeFilter matches created_after / created_before; `search` matches wa_id.
export const buildQuery = ({ state, channel, service, search, range, deciding }) => {
  const params = new URLSearchParams()
  if (state) params.append('state', state)
  // Waiting on a car decision right now (spec 0040_assistant_car_decisions Req 4.2).
  if (deciding) params.append('deciding', 'true')
  if (channel) params.append('channel', channel)
  if (service) params.append('service', service)
  if (search) params.append('search', search)
  if (range && range[0]) params.append('created_after', range[0].toISOString())
  if (range && range[1]) params.append('created_before', range[1].toISOString())
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

const LIST_TITLES = {
  duty: 'Duty conversations',
  vehicle_history: 'Vehicle history conversations',
  tracking: 'Tracking conversations'
}

// One list, mounted once per module (owner, 2026-10-29): Custom Duties, Vehicle history and
// Tracking each show only their own conversations. The service is the page's, not a filter — so
// it is never cleared with the others, and the paginator's links keep carrying it.
const ConversationsList = props => {
  const service = props.service || 'duty'
  const isDuty = service === 'duty'
  const [loading, setLoading] = useState(false)
  const [conversations, setConversations] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [state, setState] = useState(undefined)
  const [channel, setChannel] = useState(undefined)
  const [search, setSearch] = useState('')
  const [range, setRange] = useState(null)
  const [deciding, setDeciding] = useState(false)
  // Quick view: the SAME detail component the routed page renders, in a modal. Opening a whole
  // new tab to read one conversation is the thing that makes this list unusable on a phone.
  const [quickViewId, setQuickViewId] = useState(null)
  const [historyCustomerId, setHistoryCustomerId] = useState(null)

  // When `url` is supplied (a paginator next/previous link) we fetch it as-is — it already
  // carries the active filters and the page param. Otherwise we build the URL from the
  // current filters and start at page 1.
  const getConversations = async (
    filters = { state, channel, search, range, deciding },
    url = null
  ) => {
    const response = await getRequest(
      url
        ? formatUrl(url)
        : `${dutyConversationsUrl}${buildQuery({ ...filters, service })}`,
      'conversations',
      setLoading
    )
    if (response.success) {
      setConversations(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getConversations({
      state: undefined,
      channel: undefined,
      search: '',
      range: null
    })
    // eslint-disable-next-line
  }, [service])

  const applyFilters = () => getConversations({ state, channel, search, range, deciding })

  const clearFilters = () => {
    setState(undefined)
    setChannel(undefined)
    setSearch('')
    setRange(null)
    setDeciding(false)
    getConversations({
      state: undefined,
      channel: undefined,
      search: '',
      range: null
    })
  }

  // What each row is about: a duty conversation's cars, or where a history or tracking one has
  // got to (the step, and the payment step while paying).
  /* eslint-disable react/display-name */
  const aboutColumns = isDuty
    ? [
      {
        title: 'Cars',
        key: 'cars',
        render: (_, record) => (
          <span>
            {record.carCount || 0}
            {record.carStatuses && record.carStatuses.length ? (
              <div style={{ marginTop: 4 }}>
                {record.carStatuses.map((s, i) => carStatusTag(s, i))}
              </div>
            ) : null}
          </span>
        )
      },
      {
        title: 'Car Details',
        key: 'carSummaries',
        render: (_, record) => {
          const summaries = (record.carSummaries || []).filter(s => s && s.trim())
          return summaries.length ? (
            summaries.map((s, i) => <div key={i}>{s}</div>)
          ) : (
            <span style={{ color: '#888' }}>—</span>
          )
        }
      }
    ]
    : [
      {
        title: 'Progress',
        dataIndex: 'progress',
        key: 'progress',
        render: value => value || <span style={{ color: '#888' }}>—</span>
      }
    ]

  const columns = [
    {
      title: 'WhatsApp',
      key: 'waIdMasked',
      render: (_, record) => <IdentityLabel value={record} />
    },
    {
      // Beside the identity it qualifies: the number alone does not say which surface the
      // person is on, and that decides whether replying in WhatsApp will reach them.
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      render: value => channelTag(value)
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state, record) => (
        <Space size={4} wrap>
          <Tag color={STATE_COLORS[state] || 'default'}>{state}</Tag>
          {/* Waiting on a car question (spec 0040 Req 4.1); parked work (Req 7). */}
          {record.deciding ? <Tag color="processing">Deciding</Tag> : null}
          {record.parked && !record.parked.expired ? <Tag color="gold">Parked</Tag> : null}
        </Space>
      )
    },
    ...aboutColumns,
    {
      title: 'Last activity',
      dataIndex: 'lastInboundAt',
      key: 'lastInboundAt',
      render: v => (v ? new Date(v).toLocaleString() : '—')
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: v => (v ? new Date(v).toLocaleDateString() : '—')
    },
    {
      // Pinned right, so it survives the horizontal scroll this table needs on a phone — the
      // buttons are the reason anyone opens the list, and they were the first thing off-screen.
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4} wrap>
          <Button size="small" type="primary" onClick={() => setQuickViewId(record.id)}>
            View
          </Button>
          {/* Straight to the customer's history, without opening the conversation first — the
              same body the customers page shows. */}
          {record.customer ? (
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryCustomerId(record.customer)}
            >
              History
            </Button>
          ) : null}
          {/* The full page is still one tap away, for a second conversation side by side. */}
          <Link
            to={`${props.match.url}/${record.id}`.replace('//', '/')}
            target="blank"
            rel="noopener noreferrer"
            title="Open in a new tab"
          >
            <Button size="small" type="text" icon={<ExportOutlined />} >
              Link
            </Button>
          </Link>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 16 }}>
            {LIST_TITLES[service] || `${serviceLabel(service)} conversations`}{' '}
            {meta && `(${meta.count})`}
          </h3>
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder="State"
              style={{ width: 170 }}
              value={state}
              onChange={setState}
              options={STATE_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Channel"
              style={{ width: 150 }}
              value={channel}
              onChange={setChannel}
              options={CHANNEL_OPTIONS}
            />
            <RangePicker
              showTime
              value={range}
              onChange={setRange}
              placeholder={['From', 'To']}
            />
            <Checkbox checked={deciding} onChange={e => setDeciding(e.target.checked)}>
              Waiting on a decision
            </Checkbox>
            <Input.Search
              allowClear
              placeholder="Search WhatsApp number or @username"
              style={{ width: 230 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={applyFilters}
            />
            <Button type="primary" onClick={applyFilters}>
              Apply
            </Button>
            <Button onClick={clearFilters}>Clear</Button>
            <Button icon={<ReloadOutlined />} onClick={applyFilters}>
              Refresh
            </Button>
          </Space>
          <Table
            rowKey="id"
            dataSource={conversations}
            columns={columns}
            scroll={{ x: 800 }}
            pagination={false}
            locale={{ emptyText: <Empty description="No conversations yet" /> }}
          />
          {conversations.length ? (
            <PaginationContainer
              meta={meta}
              getData={url =>
                getConversations({ state, channel, search, range, deciding }, url)
              }
            />
          ) : null}
        </div>
      </div>

      {/*
        The conversation itself, in the same component the routed page uses — so everything an
        agent can do there, they can do here. `destroyOnClose` matters: the body fetches on mount,
        so closing and reopening a row gives the current state rather than what it held minutes ago.
      */}
      <Modal
        open={!!quickViewId}
        onCancel={() => setQuickViewId(null)}
        footer={null}
        width={980}
        style={{ top: 16 }}
        title="Conversation"
        destroyOnClose
      >
        {quickViewId ? (
          <ConversationDetail id={quickViewId} mode="modal" />
        ) : null}
      </Modal>

      {/* Customer history — the same body as the customers page quick view. */}
      <Modal
        open={!!historyCustomerId}
        onCancel={() => setHistoryCustomerId(null)}
        footer={null}
        width={880}
        style={{ top: 16 }}
        title="Customer history"
        destroyOnClose
      >
        {historyCustomerId ? <CustomerDetailBody id={historyCustomerId} /> : null}
      </Modal>
    </PageLoader>
  )
}

ConversationsList.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
  service: PropTypes.string
}

const DutyConversationsList = props => <ConversationsList {...props} service="duty" />
export const VehicleHistoryConversationsList = props => (
  <ConversationsList {...props} service="vehicle_history" />
)
export const TrackingConversationsList = props => (
  <ConversationsList {...props} service="tracking" />
)

const ConversationsRoutes = props => (
  <RouterSwitch>
    <PrivateRoute
      exact
      path={props.match.path}
      component={DutyConversationsList}
    />
    <PrivateRoute
      exact
      path={`${props.match.path}/:id`}
      component={ConversationDetail}
    />
  </RouterSwitch>
)

ConversationsRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(ConversationsRoutes)
